/**
 * Tiny fetch wrapper for the CRM backend. All endpoints are proxied
 * through `_redirects` to the API pod. Auth is via in-memory bearer token.
 */

import { getBearerToken } from "./auth-store";

export class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

// Optional override for how an expired session is handled (tests / embeds can
// set this). Default recovers by re-authenticating via a one-time reload.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/**
 * We had a valid token but the server rejected it (401) — the session expired
 * mid-use. Previously every subsequent action just surfaced an error toast
 * with no recovery except a manual reload. Instead, recover automatically:
 * AuthGate re-establishes the session from the `__syncsuite_session` cookie on
 * load, so a single reload silently re-auths. Guard against reload loops so a
 * genuinely-dead session doesn't spin.
 */
function handleSessionExpired(): void {
  if (onUnauthorized) { onUnauthorized(); return; }
  if (typeof window === "undefined") return;
  try {
    const KEY = "crm_reauth_at";
    const last = Number(sessionStorage.getItem(KEY) || "0");
    if (Date.now() - last > 15_000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    }
  } catch { /* sessionStorage unavailable — skip */ }
}

// Resolved once — Intl is cheap but no need to re-evaluate per request.
// Falls back to UTC if the browser is missing Intl or returns nothing
// (some old Safari builds + most CI headless envs).
const USER_TZ: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

function buildUrl(path: string, query?: Record<string, string | number | boolean | undefined | null>): string {
  const params = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      params.set(k, String(v));
    }
  }
  // Always include the user's IANA timezone so server-side date filters
  // (e.g. /tasks?due=today, /dashboards/widget-data/tasks.due_today)
  // resolve "today" against the user's local day, not the Postgres
  // server's UTC. Backend ignores the param when it isn't relevant.
  if (!params.has("tz")) params.set("tz", USER_TZ);
  const qs = params.toString();
  return `/api${path}?${qs}`;
}

async function request<T>(path: string, init?: RequestInit, query?: Record<string, string | number | boolean | undefined | null>): Promise<T> {
  const token = getBearerToken();
  const res = await fetch(buildUrl(path, query), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    // A 401 when we DID send a token means the session expired — recover by
    // re-authenticating (guarded reload). A 401 with no token is the normal
    // pre-auth state that AuthGate is already handling, so leave it alone.
    if (res.status === 401 && token) handleSessionExpired();
    let message = res.statusText;
    let data: unknown = null;
    try {
      data = await res.json();
      if (data && typeof data === "object" && "error" in data) {
        message = String((data as { error: string }).error);
      }
    } catch {
      try { message = (await res.text()) || message; } catch { /* keep statusText */ }
    }
    throw new ApiError(res.status, message, data);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get:    <T>(path: string, query?: Record<string, string | number | boolean | undefined | null>) => request<T>(path, undefined, query),
  post:   <T>(path: string, body?: unknown) => request<T>(path, { method: "POST",  body: body != null ? JSON.stringify(body) : undefined }),
  patch:  <T>(path: string, body: unknown)  => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown)  => request<T>(path, { method: "PUT",   body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
